import { apiClient } from "./apiClient";
import type { CertificationSyncReport } from "../../types/certification-sync";

export interface PushSuccessResponse {
  remote: unknown;
  report: CertificationSyncReport;
}

export interface PushErrorBody {
  kind?: "payload_incomplete" | "remote_validation" | "remote_conflict" | "auth_error" | "disabled" | "remote_error";
  detail?: string;
  errors?: Record<string, string>;
}

export const certificationSyncService = {
  /** Fetch the drift report for one producer against the Certification API. */
  async getProducerStatus(producerId: number): Promise<CertificationSyncReport> {
    const r = await apiClient.get<CertificationSyncReport>(
      `/admin/integrations/certification/producers/${producerId}/status/`,
    );
    return r.data;
  },

  /**
   * POST a producer + N farms to Certification in a single atomic call.
   * Returns the refreshed drift report on 201; on 400 the response body
   * has either `{kind: "payload_incomplete", detail}` or
   * `{kind: "remote_validation", detail, errors: {field: msg}}`.
   */
  async pushProducer(producerId: number, farmIds: number[]): Promise<PushSuccessResponse> {
    const r = await apiClient.post<PushSuccessResponse>(
      `/admin/integrations/certification/producers/${producerId}/push/`,
      { farm_ids: farmIds },
    );
    return r.data;
  },

  /**
   * PATCH editable producer fields on Certification. The response carries
   * `remote.updated_fields` so the UI can show which fields actually
   * persisted.
   */
  async updateProducer(producerId: number): Promise<PushSuccessResponse> {
    const r = await apiClient.post<PushSuccessResponse>(
      `/admin/integrations/certification/producers/${producerId}/update/`,
    );
    return r.data;
  },

  /**
   * PATCH editable farm fields on Certification. Same response shape as
   * the producer update — `remote.updated_fields` lists what persisted.
   */
  async updateFarm(farmId: number): Promise<PushSuccessResponse> {
    const r = await apiClient.post<PushSuccessResponse>(
      `/admin/integrations/certification/farms/${farmId}/update/`,
    );
    return r.data;
  },

  /**
   * Add a farm to an already-synced producer on Certification. Used for
   * farms tagged "Pendiente de crear" — the producer is in cert, but
   * this specific farm hasn't been pushed yet.
   */
  async createFarm(farmId: number): Promise<PushSuccessResponse> {
    const r = await apiClient.post<PushSuccessResponse>(
      `/admin/integrations/certification/farms/${farmId}/create/`,
    );
    return r.data;
  },
};
